import type { DashboardRevenueByAppSchema } from "@biteasy/contracts";
import { ResponsivePie } from "@nivo/pie";
import type { z } from "zod";
import { formatCurrency } from "@/util/format";

interface RevenueByAppChartProps {
	data: z.infer<typeof DashboardRevenueByAppSchema>;
	currency?: string;
}

const COLORS = [
	"#10b981", // emerald
	"#3b82f6", // blue
	"#8b5cf6", // violet
	"#f59e0b", // amber
	"#ef4444", // red
	"#ec4899", // pink
	"#06b6d4", // cyan
	"#84cc16", // lime
];

/**
 * Pie/donut chart showing revenue breakdown by app
 */
export function RevenueByAppChart({
	data,
	currency = "usd",
}: RevenueByAppChartProps) {
	if (data.items.length === 0) {
		return (
			<div className="flex h-75 items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50">
				<p className="text-sm text-base-content/60">No revenue data yet</p>
			</div>
		);
	}

	const chartData = data.items.map((item, index) => ({
		id: item.appName,
		label: item.appName,
		value: item.revenue / 100, // Convert to dollars for display
		color: COLORS[index % COLORS.length],
		percentage: item.percentage,
	}));

	return (
		<div className="flex h-96 flex-col justify-between rounded-xl border border-base-content/10 bg-base-100 p-4">
			<div className="flex-1 min-h-0">
				<ResponsivePie
					data={chartData}
					margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
					innerRadius={0.6}
					padAngle={0.5}
					cornerRadius={4}
					activeOuterRadiusOffset={8}
					colors={{ datum: "data.color" }}
					borderWidth={1}
					borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
					enableArcLinkLabels={false}
					arcLabelsSkipAngle={20}
					arcLabelsTextColor={{
						from: "color",
						modifiers: [["darker", 2]],
					}}
					tooltip={({ datum }) => (
						<div className="rounded-lg border border-base-content/10 bg-base-100 p-3 shadow-xl">
							<div className="flex items-center gap-2">
								<div
									className="size-3 rounded-full"
									style={{ backgroundColor: datum.color }}
								/>
								<span className="text-sm font-medium text-base-content">
									{datum.label}
								</span>
							</div>
							<div className="mt-1 text-sm font-bold text-base-content">
								{formatCurrency((datum.value as number) * 100, currency)}
							</div>
							<div className="text-xs text-base-content/60">
								{datum.data.percentage?.toFixed(1)}% of total
							</div>
						</div>
					)}
					theme={{
						text: {
							fill: "var(--color-base-content)",
							fillOpacity: 0.8,
							fontSize: 12,
						},
						tooltip: {
							container: {
								background: "transparent",
								padding: 0,
								borderRadius: 0,
								boxShadow: "none",
							},
						},
					}}
				/>
			</div>

			<div className="mt-4 flex flex-col gap-2 overflow-y-auto px-2 max-h-32 scrollbar-thin scrollbar-thumb-base-content/10 scrollbar-track-transparent">
				{chartData.map((item) => (
					<div
						key={item.id}
						className="flex items-center justify-between gap-4"
					>
						<div className="flex items-center gap-2 min-w-0">
							<div
								className="size-2.5 flex-none rounded-full"
								style={{ backgroundColor: item.color }}
							/>
							<span className="truncate text-xs font-medium text-base-content/80">
								{item.label}
							</span>
						</div>
						<div className="flex items-center gap-3 flex-none">
							<span className="text-xs font-medium text-base-content">
								{formatCurrency(item.value * 100, currency)}
							</span>
							<span className="text-xs text-base-content/50 w-10 text-right">
								{item.percentage?.toFixed(1)}%
							</span>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
